import { useLanguage } from "@/components/language-provider";
import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { SiGoogle } from "react-icons/si";
// import { Building2 } from "lucide-react"; // Comentado - Microsoft login desabilitado temporariamente
import { BrandLogo } from "@/components/brand-logo";
import { API_BASE_URL } from "@/lib/config";

export default function Login() {
  const { t, language } = useLanguage();
  const [, setLocation] = useLocation();

  // Check for redirect parameter (used by D365 integration)
  const redirectPath = new URLSearchParams(window.location.search).get("redirect");
  // Sent here by the API after an admin disabled the account (401 USER_DISABLED on any request).
  const disabledReason = new URLSearchParams(window.location.search).get("reason") === "disabled";

  // ... existing state below
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      
      const text = await res.text();
      
      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch (parseError) {
        console.error("JSON parse error:", parseError);
        throw new Error("Server returned invalid response");
      }
      
      // Check for email verification requirement FIRST (even on success=false)
      if (data.requiresEmailVerification) {
        toast({ 
          title: "Email não verificado", 
          description: data.message || "Por favor, verifique seu email antes de fazer login.", 
          variant: "destructive" 
        });
        setLoading(false);
        return;
      }
      
      if (data.errorCode === "USER_DISABLED") {
        toast({
          title: language === "pt" ? "Conta desabilitada" : "Account disabled",
          description: language === "pt" ? data.messagePt : data.messageEn,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      if (!res.ok) {
        throw new Error(data.message || data.errors?.[0]?.message || "Login failed");
      }
      
      // Handle different response formats:
      // Format 1: { data: { user, token } } (wrapped)
      // Format 2: { user, token } (direct)
      // Format 3: { User, Token } (PascalCase)
      const responseData = data.data || data;
      const token = responseData.token || responseData.Token;
      const user = responseData.user || responseData.User;
      
      if (!token || !user) {
        console.error("Login: missing token or user in response");
        throw new Error("Invalid response from server - missing token or user");
      }
      
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      
      // If user must change password (invited user), redirect to profile
      if (user.mustChangePassword) {
        setLocation("/profile");
      } else {
        let target = redirectPath || "/dashboard";
        try {
          const pmRes = await fetch(`${API_BASE_URL}/api/subscriptions/payment-method-status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (pmRes.ok) {
            const pm = await pmRes.json();
            if (pm.required && !pm.verified) target = "/verify-payment";
          }
        } catch { /* fail open */ }
        try {
          const phRes = await fetch(`${API_BASE_URL}/api/auth/phone-status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (phRes.ok) {
            const ph = await phRes.json();
            if (ph.required && !ph.verified && target === "/dashboard") target = "/verify-phone";
          }
        } catch { /* fail open */ }
        if (user?.language && user.language !== localStorage.getItem("language")) {
          localStorage.setItem("language", user.language);
          window.location.href = target; // full reload applies the language
          return;
        }
        setLocation(target);
      }
    } catch (error: any) {
      console.error("Login error:", error);
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setOauthLoading("google");
    try {
      const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      console.log("Google Client ID:", googleClientId ? "configured" : "NOT CONFIGURED");
      
      if (!googleClientId) {
        toast({ 
          title: t("ui.configuration_error"), 
          description: t("ui.google_oauth_is_not_configured_please_contact_su"), 
          variant: "destructive" 
        });
        setOauthLoading(null);
        return;
      }
      
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      const scope = "openid email profile";
      // Use response_type=id_token token to get both tokens
      // nonce is required for id_token
      const nonce = Math.random().toString(36).substring(2, 15);
      sessionStorage.setItem("oauth_nonce", nonce);
      
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${googleClientId}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&response_type=id_token token` +
        `&scope=${encodeURIComponent(scope)}` +
        `&nonce=${nonce}`;
      
      console.log("Redirecting to Google OAuth...");
      window.location.href = authUrl;
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
      setOauthLoading(null);
    }
  };

  // =====================================================
  // MICROSOFT LOGIN - COMENTADO TEMPORARIAMENTE
  // Descomentar quando quiser habilitar o login com Microsoft
  // =====================================================
  /*
  const handleMicrosoftLogin = async () => {
    setOauthLoading("microsoft");
    try {
      const msClientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID;
      if (!msClientId) {
        toast({ 
          title: t("ui.configuration_error"), 
          description: t("ui.microsoft_oauth_is_not_configured_please_contact"), 
          variant: "destructive" 
        });
        return;
      }
      
      const redirectUri = `${window.location.origin}/auth/microsoft/callback`;
      const scope = "openid email profile User.Read";
      const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${msClientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=token&scope=${encodeURIComponent(scope)}`;
      
      window.location.href = authUrl;
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    } finally {
      setOauthLoading(null);
    }
  };
  */

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Link href="/" className="flex items-center justify-center mb-4">
            <BrandLogo />
          </Link>
          <CardTitle>{t("ui.welcome_back")}</CardTitle>
          <CardDescription>{t("ui.sign_in_to_your_account_to_continue")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* OAuth Buttons */}
          <div className="flex justify-center">
            {/* Google Login Button */}
            <Button 
              variant="outline" 
              onClick={handleGoogleLogin}
              disabled={oauthLoading !== null}
              className="w-full max-w-xs"
              data-testid="button-google-login"
            >
              {oauthLoading === "google" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <SiGoogle className="mr-2 h-4 w-4" />
              )}
              {t("ui.continue_with_google")}
            </Button>
            
            {/* =====================================================
                MICROSOFT LOGIN BUTTON - COMENTADO TEMPORARIAMENTE
                Descomentar quando quiser habilitar
                ===================================================== */}
            {/*
            <Button 
              variant="outline" 
              onClick={handleMicrosoftLogin}
              disabled={oauthLoading !== null}
              data-testid="button-microsoft-login"
            >
              {oauthLoading === "microsoft" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Building2 className="mr-2 h-4 w-4" />
              )}
              Microsoft
            </Button>
            */}
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">{t("ui.or_continue_with_email")}</span>
            </div>
          </div>
          
          {disabledReason && (
            <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive" role="alert" data-testid="login-disabled-alert">
              {language === "pt" ? "Esta conta foi desabilitada. Fale com o administrador." : "This account is disabled. Contact the administrator."}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("ui.email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="input-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("ui.password")}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t("ui.enter_your_password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="input-password"
              />
            </div>
            <div className="flex justify-end -mt-2">
              <Link href="/forgot-password" className="text-sm text-primary hover:underline" data-testid="link-forgot-password">
                {t("ui.forgot_your_password")}
              </Link>
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="button-submit">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("ui.sign_in")}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link href="/register" className="text-primary hover:underline" data-testid="link-register">
              {t("ui.sign_up")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
